import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { from, Observable, of, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/auth.service';
import { DemoCasesService } from './demo-cases.service';
import { DemoCaseBundle } from './demo-case-bundle';
import {
  DemoRequestContext,
  DemoRequestNotFound,
  getAnalysisStatus,
  getChatById,
  getFileActionsForMessage,
  getFileActionsGroupedByEvent,
  getFileById,
  getFileMeta,
  getFileVersions,
  getFilesInTrashbin,
  getMessageById,
  getSourceById,
  getStateById,
  listChatsInCase,
  listFiles,
  listTimelineEventsInCase,
  queryChatMessages,
} from './demo-api.handlers';
import { toCaseDefinition } from './demo-api.mappers';

/**
 * Serves API requests for demo cases from their exported JSON instead of the backend.
 *
 * This sits at the HTTP layer rather than in the API services because roughly fifty call sites
 * inject those services, several of them from root-scoped singletons that are already
 * constructed by the time a case opens. Intercepting below all of that means no feature code
 * has to know demo mode exists.
 *
 * Requests that are not for a loaded demo case fall straight through; the guard is a single
 * signal read, so nothing measurable happens on the normal path.
 */
export const demoApiInterceptor: HttpInterceptorFn = (request, next) => {
  const demoCases = inject(DemoCasesService);
  if (!demoCases.active()) return next(request);

  const path = apiPath(request.url);
  if (path === null) return next(request);

  const auth = inject(AuthService);
  const resolved = resolveDemoRequest(demoCases, request, path, auth.isStaff);
  if (resolved === null) return next(request);

  return resolved;
};

/** The path of an API request relative to `apiUrl`, or `null` if it isn't one. */
function apiPath(url: string): string | null {
  const base = environment.apiUrl.replace(/\/+$/, '');
  if (!url.startsWith(base)) return null;
  const withoutBase = url.slice(base.length).replace(/^\/+/, '');
  return withoutBase.split('?')[0];
}

function ok<T>(body: T): Observable<HttpEvent<unknown>> {
  return of(new HttpResponse({ status: 200, body: body as unknown }));
}

/**
 * Wraps a handler that resolves synchronously but can throw `DemoRequestNotFound` (unlike
 * `fromPromise`'s handlers, whose rejection already becomes an observable error). Without this,
 * that throw would happen while building the observable, not inside it, and surface as an
 * unhandled exception instead of a 404 response.
 */
function okSync<T>(compute: () => T): Observable<HttpEvent<unknown>> {
  try {
    return ok(compute());
  } catch (error) {
    if (error instanceof DemoRequestNotFound) return throwError(() => notFound(error.message));
    throw error;
  }
}

function fromPromise<T>(promise: Promise<T>): Observable<HttpEvent<unknown>> {
  return from(
    promise.then(
      (body) => new HttpResponse({ status: 200, body: body as unknown }),
      (error) => {
        throw error instanceof DemoRequestNotFound ? notFound(error.message) : error;
      },
    ),
  );
}

function notFound(message: string): HttpErrorResponse {
  return new HttpErrorResponse({ status: 404, statusText: 'Not Found', error: { message } });
}

/**
 * Demo cases are read-only. Rejecting every mutation here means a control that was missed in
 * the UI can annoy a user but can never write to a real case.
 */
function readOnly(request: HttpRequest<unknown>): Observable<never> {
  return throwError(
    () =>
      new HttpErrorResponse({
        status: 403,
        statusText: 'Forbidden',
        url: request.url,
        error: { message: 'Demo cases are read-only.' },
      }),
  );
}

/**
 * Maps a request onto a handler, or returns `null` to let it through.
 *
 * Returning `null` matters for endpoints that are not case-scoped (account, limits, skills,
 * templates, …): those keep talking to the real API even while a demo case is open, which is
 * what makes the demo case a drop-in rather than a separate mode of the whole app.
 */
function resolveDemoRequest(
  demoCases: DemoCasesService,
  request: HttpRequest<unknown>,
  path: string,
  isAdminViewer: boolean,
): Observable<HttpEvent<unknown>> | null {
  const segments = path.split('/').filter(Boolean);
  const params = request.params;
  const contextFor = (bundle: DemoCaseBundle): DemoRequestContext => ({ bundle, isAdminViewer });

  switch (segments[0]) {
    case 'cases':
      return resolveCaseRequest(demoCases, request, segments, params);
    case 'files':
      return resolveFileRequest(demoCases, request, segments, params);
    case 'chats':
      return resolveChatRequest(demoCases, request, segments);
    case 'states':
      return resolveStateRequest(demoCases, request, segments, params, contextFor);
    default:
      return null;
  }
}

function resolveCaseRequest(
  demoCases: DemoCasesService,
  request: HttpRequest<unknown>,
  segments: string[],
  params: HttpParams,
): Observable<HttpEvent<unknown>> | null {
  const [, caseId, subResource] = segments;
  if (!caseId) return null; // `GET /cases` (the overview list) still comes from the API.

  const bundle = demoCases.bundleForCase(caseId);
  if (!bundle) return null;
  if (request.method !== 'GET') return readOnly(request);

  switch (subResource) {
    case undefined:
      // `bundle.caseData.id` is whatever the export baked into case.json, which is not
      // guaranteed to match the id the case is indexed/routed under (bundle.caseId) — and it's
      // the latter that isDemoCase() and every other lookup here key off of. Report that one.
      return ok({ ...toCaseDefinition(bundle.caseData), id: bundle.caseId });
    case 'chats':
      return ok(listChatsInCase(bundle, params));
    case 'timeline':
      return ok(listTimelineEventsInCase(bundle, params));
    case 'sources':
      return okSync(() => getSourceById(bundle, params.get('source_id') ?? ''));
    default:
      return null;
  }
}

function resolveFileRequest(
  demoCases: DemoCasesService,
  request: HttpRequest<unknown>,
  segments: string[],
  params: HttpParams,
): Observable<HttpEvent<unknown>> | null {
  const [, second, third] = segments;

  // `GET /files?case_id=…` — the document listing. `POST /files` is an upload, whose case is in
  // the form body rather than the query, so it is left to fail against the real API.
  if (!second) {
    const bundle = demoCases.bundleForCase(params.get('case_id'));
    if (!bundle) return null;
    return request.method === 'GET' ? ok(listFiles(bundle, params)) : readOnly(request);
  }

  if (second === 'analysis-status') {
    // Case-scoped: without a case id this is a cross-case question only the API can answer.
    const bundle = demoCases.bundleForCase(params.get('case_id'));
    return bundle ? ok(getAnalysisStatus(bundle, params)) : null;
  }

  if (second === 'folders' || second === 'temp' || second === 'duplicates' || second === 'path') {
    return null;
  }

  // `/files/<caseId>/trashbin` is addressed by case, every other route by file.
  if (third === 'trashbin') {
    const bundle = demoCases.bundleForCase(second);
    return bundle ? ok(getFilesInTrashbin(params)) : null;
  }

  const bundle = demoCases.bundleForFile(second);
  if (!bundle) return null;
  if (request.method !== 'GET') return readOnly(request);

  switch (third) {
    case undefined:
      // The raw-content route; the app builds this URL directly rather than through HttpClient,
      // so this only guards the metadata read that shares the path.
      return okSync(() => getFileById(bundle, second));
    case 'meta':
      return fromPromise(getFileMeta(bundle, second));
    case 'versions':
      return okSync(() => getFileVersions(bundle, second));
    case 'download':
      return ok({ download_link: bundle.fileContentUrl(second) });
    default:
      return null;
  }
}

function resolveChatRequest(
  demoCases: DemoCasesService,
  request: HttpRequest<unknown>,
  segments: string[],
): Observable<HttpEvent<unknown>> | null {
  const [, chatId, subResource] = segments;
  if (!chatId) return null;

  const bundle = demoCases.bundleForChat(chatId);
  if (!bundle) return null;
  if (request.method !== 'GET') return readOnly(request);
  if (subResource !== undefined) return null;

  return okSync(() => getChatById(bundle, chatId));
}

function resolveStateRequest(
  demoCases: DemoCasesService,
  request: HttpRequest<unknown>,
  segments: string[],
  params: HttpParams,
  contextFor: (bundle: DemoCaseBundle) => DemoRequestContext,
): Observable<HttpEvent<unknown>> | null {
  const [, stateId, subResource, subId, action] = segments;
  if (!stateId) return null;

  // `/states/file-actions/<id>/update-status` approves or rejects a document edit. Refusing it
  // here turns what would otherwise be a confusing 404 from the real API into a clear "read-only".
  // The batch variant carries its ids in the body and is left to fail against the API.
  if (stateId === 'file-actions') {
    return subResource && demoCases.bundleForFileAction(subResource) ? readOnly(request) : null;
  }

  const bundle = demoCases.bundleForState(stateId);
  if (!bundle) return null;
  if (request.method !== 'GET') return readOnly(request);

  if (subResource === undefined) {
    return fromPromise(getStateById(bundle, stateId));
  }

  if (subResource === 'branches' && subId) {
    if (action === 'messages') {
      return fromPromise(queryChatMessages(contextFor(bundle), stateId, subId, params));
    }
    if (action === 'file-actions') {
      return fromPromise(getFileActionsGroupedByEvent(bundle, stateId, subId));
    }
    return null;
  }

  if (subResource === 'messages' && subId) {
    if (action === undefined) {
      return fromPromise(getMessageById(contextFor(bundle), stateId, subId));
    }
    if (action === 'file_actions') {
      return ok(getFileActionsForMessage(bundle, subId));
    }
    return null;
  }

  return null;
}
